import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, type ImageProbeResult, type OnboardManagedInput, api } from "../lib/api";
import {
  type EnvDraft,
  type VolumeDraft,
  envFromDraft,
  secretsFromDraft,
  suffixFromPath,
  volumesFromDrafts,
} from "../lib/onboard";
import { slugFromName } from "../lib/slug";
import { InfoTooltip } from "./InfoTooltip";

export interface OnboardWizardProps {
  onCancel: () => void;
  onCreated: (slug: string) => void;
  /** Which surface to call: admin endpoints or the gated user ones (default). */
  endpoint?: "admin" | "user";
}

type SlugCheck = "idle" | "checking" | "ok" | "taken" | "invalid" | "reserved";

export function OnboardWizard(props: OnboardWizardProps): JSX.Element {
  const { t } = useTranslation();
  const probeFn = props.endpoint === "admin" ? api.admin.probeImage : api.probeImage;
  const onboardFn = props.endpoint === "admin" ? api.admin.onboard : api.onboard;
  const domain = window.location.origin;
  const imageRefId = useId();
  const nameId = useId();
  const slugId = useId();

  const [step, setStep] = useState<"url" | "review">("url");
  const [imageRef, setImageRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [probe, setProbe] = useState<ImageProbeResult | null>(null);

  // Editable review state — Name drives the slug unless the slug is hand-edited.
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [deploy, setDeploy] = useState(true);
  const [visibility, setVisibility] = useState<"private" | "public">("public");
  const [envDraft, setEnvDraft] = useState<EnvDraft[]>([]);
  const [volumeDraft, setVolumeDraft] = useState<VolumeDraft[]>([]);
  const [slugCheck, setSlugCheck] = useState<SlugCheck>("idle");

  // Live availability check (debounced) — like GitHub's repo-name check.
  useEffect(() => {
    if (step !== "review") return;
    if (!slug) {
      setSlugCheck("invalid");
      return;
    }
    setSlugCheck("checking");
    const handle = setTimeout(() => {
      api
        .checkSlug(slug)
        .then((res) => setSlugCheck(res.available ? "ok" : (res.reason as SlugCheck)))
        .catch(() => setSlugCheck("idle"));
    }, 400);
    return () => clearTimeout(handle);
  }, [slug, step]);

  const onNameChange = (value: string): void => {
    setName(value);
    if (!slugTouched) setSlug(slugFromName(value));
  };

  const onSlugChange = (value: string): void => {
    setSlugTouched(true);
    setSlug(slugFromName(value));
  };

  const onProbe = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      const result = await probeFn(imageRef.trim());
      setProbe(result);
      setName(result.suggested_name);
      setSlug(result.suggested_slug);
      setSlugTouched(false);
      setEnvDraft(
        result.env_template.entries.map((e) => ({
          key: e.key,
          value: e.default_value ?? "",
          isSecret: e.is_secret_hint,
          comment: e.comment,
          include: true,
        })),
      );
      // #7: Aus den deklarierten VOLUME-Pfaden Volume-Vorschlaege bauen (default
      // an). Suffixe kollisionsfrei machen (data, data-2, ...).
      const usedSuffixes = new Set<string>();
      setVolumeDraft(
        result.volume_paths.map((path) => {
          const base = suffixFromPath(path);
          let suffix = base;
          let n = 2;
          while (usedSuffixes.has(suffix)) suffix = `${base}-${n++}`;
          usedSuffixes.add(suffix);
          return { suffix, container_path: path, include: true };
        }),
      );
      setStep("review");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      const payload: OnboardManagedInput = {
        slug: slug.trim(),
        name: name.trim(),
        proxy_settings: {},
        // Container port is auto-detected from the image's EXPOSE at deploy.
        visibility,
        runtime: {
          image_ref: imageRef.trim(),
          ...envFromDraft(envDraft),
          ...secretsFromDraft(envDraft),
          ...volumesFromDrafts(volumeDraft, slug.trim()),
        },
        deploy,
      };
      const result = await onboardFn(payload);
      props.onCreated(result.app.app.slug);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const slugMessage: Record<SlugCheck, string> = {
    idle: "",
    checking: t("admin.onboard.slugChecking"),
    ok: t("admin.onboard.slugFree"),
    taken: t("admin.onboard.slugTaken"),
    invalid: t("admin.onboard.slugInvalid"),
    reserved: t("admin.onboard.slugReserved"),
  };

  return (
    <div className="admin__form onboard">
      <div className="onboard__head">
        <h3>{t("admin.onboard.title")}</h3>
        <span className="onboard__step">
          {t("admin.onboard.step", { n: step === "url" ? 1 : 2, total: 2 })}
        </span>
      </div>
      <p className="onboard__req-note">{t("common.requiredHint")}</p>
      {error ? <p className="launchpad__hint launchpad__hint--error">{error}</p> : null}

      {step === "url" ? (
        <>
          <p className="onboard__instruction">{t("admin.onboard.urlInstruction")}</p>
          <div className="admin__field">
            <span className="field-head">
              <label htmlFor={imageRefId}>
                {t("admin.onboard.packageUrl")}
                <span className="field-req" aria-hidden="true">
                  *
                </span>
              </label>
              <InfoTooltip text={t("admin.onboard.packageUrlHint")} />
            </span>
            <input
              id={imageRefId}
              type="text"
              required
              value={imageRef}
              placeholder="ghcr.io/owner/example-app:latest"
              onChange={(e) => setImageRef(e.target.value)}
            />
          </div>
          <div className="admin__form-actions">
            <button type="button" onClick={props.onCancel} disabled={busy}>
              {t("admin.cancel")}
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => void onProbe()}
              disabled={busy || !imageRef.trim()}
            >
              {busy ? t("admin.onboard.probing") : t("admin.onboard.probe")}
            </button>
          </div>
        </>
      ) : null}

      {step === "review" && probe ? (
        <>
          <p className="onboard__instruction">{t("admin.onboard.reviewInstruction")}</p>
          <div className="admin__field">
            <span className="field-head">
              <label htmlFor={nameId}>
                {t("admin.onboard.appName")}
                <span className="field-req" aria-hidden="true">
                  *
                </span>
              </label>
            </span>
            <input
              id={nameId}
              type="text"
              required
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
            />
          </div>

          <div className="admin__field">
            <span className="field-head">
              <label htmlFor={slugId}>
                {t("admin.onboard.reachableAt")}
                <span className="field-req" aria-hidden="true">
                  *
                </span>
              </label>
              <InfoTooltip text={t("admin.onboard.reachableAtHint")} />
            </span>
            <span className="onboard__url">
              <span className="onboard__url-fixed">{domain}/</span>
              <input
                id={slugId}
                className="onboard__url-slug"
                type="text"
                required
                value={slug}
                onChange={(e) => onSlugChange(e.target.value)}
              />
              <span className="onboard__url-fixed">/</span>
            </span>
            <p
              className={
                slugCheck === "ok"
                  ? "onboard__slug-msg onboard__slug-msg--ok"
                  : slugCheck === "checking" || slugCheck === "idle"
                    ? "onboard__slug-msg"
                    : "onboard__slug-msg onboard__slug-msg--err"
              }
            >
              {slugMessage[slugCheck]}
            </p>
          </div>

          {envDraft.length > 0 ? (
            <fieldset className="admin__field">
              <legend>
                {t("admin.onboard.env")}
                <InfoTooltip text={t("admin.onboard.secretHint")} />
              </legend>
              <div className="onboard__env-bulk">
                <span>{t("admin.onboard.envSelect")}</span>
                <button
                  type="button"
                  onClick={() => setEnvDraft((prev) => prev.map((x) => ({ ...x, include: true })))}
                >
                  {t("admin.onboard.envSelectAll")}
                </button>
                <button
                  type="button"
                  onClick={() => setEnvDraft((prev) => prev.map((x) => ({ ...x, include: false })))}
                >
                  {t("admin.onboard.envSelectNone")}
                </button>
              </div>
              {envDraft.map((d, i) => (
                <div key={d.key} className="admin__field admin__field--inline onboard__env-row">
                  <label className="onboard__env-include">
                    <input
                      type="checkbox"
                      checked={d.include}
                      aria-label={t("admin.onboard.envInclude", { key: d.key })}
                      onChange={(e) =>
                        setEnvDraft((prev) =>
                          prev.map((x, j) => (j === i ? { ...x, include: e.target.checked } : x)),
                        )
                      }
                    />
                    <code>{d.key}</code>
                  </label>
                  {d.isSecret ? (
                    <>
                      <span className="badge">{t("admin.onboard.secret")}</span>
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={d.value}
                        disabled={!d.include}
                        placeholder={t("admin.onboard.secretValuePlaceholder")}
                        onChange={(e) =>
                          setEnvDraft((prev) =>
                            prev.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)),
                          )
                        }
                      />
                    </>
                  ) : (
                    <input
                      type="text"
                      value={d.value}
                      disabled={!d.include}
                      onChange={(e) =>
                        setEnvDraft((prev) =>
                          prev.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)),
                        )
                      }
                    />
                  )}
                  {d.comment ? <span className="onboard__env-hint">{d.comment}</span> : null}
                </div>
              ))}
            </fieldset>
          ) : (
            <p className="onboard__notice onboard__notice--warn">
              {t("admin.onboard.noEnvTemplate")}
            </p>
          )}

          <fieldset className="admin__field">
            <legend>{t("admin.onboard.volumes")}</legend>
            <p className="onboard__env-hint">{t("admin.onboard.volumesHint")}</p>
            {volumeDraft.map((v, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: append-only list (toggle, kein Loeschen/Reorder) -> Index ist stabil.
              <div key={i} className="admin__field admin__field--inline onboard__env-row">
                <label className="onboard__env-include">
                  <input
                    type="checkbox"
                    checked={v.include}
                    aria-label={t("admin.onboard.volumeInclude", {
                      name: `appdata-${slug}-${v.suffix}`,
                    })}
                    onChange={(e) =>
                      setVolumeDraft((prev) =>
                        prev.map((x, j) => (j === i ? { ...x, include: e.target.checked } : x)),
                      )
                    }
                  />
                  <code>appdata-{slug}-</code>
                </label>
                <input
                  type="text"
                  value={v.suffix}
                  disabled={!v.include}
                  placeholder="data"
                  aria-label={t("admin.onboard.volumeSuffix")}
                  onChange={(e) =>
                    setVolumeDraft((prev) =>
                      prev.map((x, j) => (j === i ? { ...x, suffix: e.target.value } : x)),
                    )
                  }
                />
                <input
                  type="text"
                  value={v.container_path}
                  disabled={!v.include}
                  placeholder="/data"
                  aria-label={t("admin.onboard.volumePath")}
                  onChange={(e) =>
                    setVolumeDraft((prev) =>
                      prev.map((x, j) => (j === i ? { ...x, container_path: e.target.value } : x)),
                    )
                  }
                />
              </div>
            ))}
            <button
              type="button"
              className="onboard__env-add"
              onClick={() =>
                setVolumeDraft((prev) => [
                  ...prev,
                  { suffix: "", container_path: "", include: true },
                ])
              }
            >
              {t("admin.onboard.volumeAdd")}
            </button>
          </fieldset>

          <fieldset className="admin__field">
            <legend>{t("onboard.visibility.label")}</legend>
            <label className="admin__field--inline">
              <input
                type="radio"
                name="onboard-visibility"
                checked={visibility === "public"}
                onChange={() => setVisibility("public")}
              />
              {t("onboard.visibility.public")}
            </label>
            <label className="admin__field--inline">
              <input
                type="radio"
                name="onboard-visibility"
                checked={visibility === "private"}
                onChange={() => setVisibility("private")}
              />
              {t("onboard.visibility.private")}
            </label>
          </fieldset>

          <div className="admin__field admin__field--inline">
            <label className="admin__field--inline">
              <input
                type="checkbox"
                checked={deploy}
                onChange={(e) => setDeploy(e.target.checked)}
              />
              {t("admin.onboard.deployNow")}
            </label>
            <InfoTooltip text={t("admin.onboard.deployHint")} />
          </div>

          <div className="admin__form-actions">
            <button type="button" onClick={() => setStep("url")} disabled={busy}>
              {t("admin.onboard.back")}
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => void onConfirm()}
              disabled={busy || !name.trim() || slugCheck !== "ok"}
            >
              {busy ? t("admin.onboard.creating") : t("admin.onboard.confirm")}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.detail ?? e.message;
  return e instanceof Error ? e.message : String(e);
}
